import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { loadModules } from 'esri-loader';
import marker from '../../../CustomVestmapViewer/assets/marker center.png';

function GenericSectionMap({ webmapID, token, addressInfo }) {
  const mapRef = useRef(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const viewRef = useRef(null);

  useEffect(() => {
    if (!webmapID || !token || !addressInfo) return;

    let view;
    loadModules([
      'esri/Map',
      'esri/views/MapView',
      'esri/WebMap',
      'esri/geometry/Extent',
      'esri/Graphic',
      'esri/widgets/Legend',
      'esri/config',
    ]).then(
      ([Map, MapView, WebMap, Extent, Graphic, Legend, esriConfig]) => {
        if (!webmapID || !token || !mapRef.current) return;

        esriConfig.apiKey = token;

        const extent = new Extent({
          xmin: addressInfo.address_xmin,
          ymin: addressInfo.address_ymin,
          xmax: addressInfo.address_xmax,
          ymax: addressInfo.address_ymax,
          spatialReference: {
            wkid: addressInfo.spatialReference?.wkid || 4326,
          },
        });

        const webmap = new WebMap({
          portalItem: {
            id: webmapID,
          },
        });

        view = new MapView({
          container: mapRef.current,
          map: webmap,
          extent: extent,
          zoom: 14,
          navigation: {
            mouseWheelZoomEnabled: false,
          },
        });

        viewRef.current = view;

        view
          .when(() => {
            setIsMapLoaded(true);
            setErrorMessage(null);

            view.ui.add(
              new Legend({
                view: view,
              }),
              'top-right',
            );

            if (
              addressInfo.address_longitude &&
              addressInfo.address_latitude
            ) {
              const customSymbol = {
                type: 'picture-marker',
                url: marker,
                width: '50px',
                height: '100px',
              };

              view.graphics.add(
                new Graphic({
                  geometry: {
                    type: 'point',
                    longitude: addressInfo.address_longitude,
                    latitude: addressInfo.address_latitude,
                  },
                  symbol: customSymbol,
                }),
              );
            }
          })
          .catch(err => {
            console.error('Error loading generic section map:', err);
            setIsMapLoaded(false);
            setErrorMessage(err.message || 'Failed to load map');
          });
      },
    );

    return () => {
      if (viewRef.current) {
        viewRef.current.destroy();
      }
    };
  }, [webmapID, token, addressInfo]);

  return (
    <Box
      sx={{
        width: '100%',
        height: {
          sm: '30rem',
          xs: '20rem',
        },
        position: 'relative',
      }}
    >
      {!isMapLoaded && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
          }}
        >
          {errorMessage ? (
            <Typography
              variant='body2'
              sx={{ color: '#b00020', textAlign: 'center', p: 2 }}
            >
              {errorMessage}
            </Typography>
          ) : (
            <CircularProgress sx={{ color: '#4FA490' }} />
          )}
        </Box>
      )}
      <Box
        ref={mapRef}
        sx={{
          width: '100%',
          height: '100%',
          visibility: isMapLoaded ? 'visible' : 'hidden',
        }}
      />
    </Box>
  );
}

const GenericSectionCard = ({
  type,
  addressInfo,
  webmapID,
  arcgisToken,
  blocks = [],
  sectionTitle = 'Section',
}) => {
  if (type === 'map') {
    if (!webmapID || !arcgisToken || !addressInfo) {
      return (
        <Box
          sx={{
            height: '256px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#F3F4F6',
            borderRadius: '0.5rem',
            border: '2px dashed #D1D5DB',
          }}
        >
          <Typography sx={{ fontSize: '0.875rem', color: '#6B7280' }}>
            Search for an address to view map
          </Typography>
        </Box>
      );
    }

    return (
      <GenericSectionMap
        webmapID={webmapID}
        token={arcgisToken}
        addressInfo={addressInfo}
      />
    );
  }

  if (type === 'data') {
    const blockList = Array.isArray(blocks) ? blocks : [];

    if (blockList.length === 0) {
      return (
        <Box
          sx={{
            height: '256px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#F3F4F6',
            borderRadius: '0.5rem',
            border: '2px dashed #D1D5DB',
          }}
        >
          <Typography sx={{ fontSize: '0.875rem', color: '#6B7280' }}>
            Search for an address to view {sectionTitle} data
          </Typography>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
        }}
      >
        {blockList.map((block, index) => (
          <Box
            key={block.key || index}
            sx={{
              flex: 1,
              backgroundColor: '#F9F9F9',
              padding: '1rem 1.75rem',
              borderRadius: '1rem',
            }}
          >
            <Typography
              sx={{
                fontSize: '0.75rem',
                fontWeight: 600,
                color: '#6B7280',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                marginBottom: '0.5rem',
              }}
            >
              {block.title || block.key || 'Block'}
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Lato',
                fontSize: '1rem',
                fontWeight: 400,
                color: '#111827',
              }}
            >
              {block.value != null && block.value !== ''
                ? String(block.value)
                : '—'}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  }

  return null;
};

export default GenericSectionCard;
